import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/api';
import CampCard from '../components/CampCard';
import type { Camp } from '../types/database';
import toast from 'react-hot-toast';
import { useSession } from '@supabase/auth-helpers-react';
import { useNavigate } from 'react-router-dom';
import SEO from '../components/SEO';
import { HeartIcon } from '@heroicons/react/24/outline';
import LoadingState from '../components/LoadingState';

export default function FavoriteCampsPage() {
  const session = useSession();
  const navigate = useNavigate();

  const { data: camps = [], isLoading, refetch } = useQuery(
    ['favorite-camps'],
    () => api.camps.getFavorites(),
    {
      enabled: !!session,
      onError: (error) => {
        console.error('Error in favorite camps query:', error);
        toast.error('Failed to load favorite camps');
      }
    }
  );

  if (!session) {
    return (
      <div className="page-container">
        <div className="page-content">
          <div className="flex items-center justify-center min-h-[60vh]">
            <div className="text-center">
              <h1 className="text-2xl font-semibold text-gray-900 mb-4">Please sign in to view your favorite camps</h1>
              <button
                onClick={() => navigate('/login')}
                className="btn btn-primary"
              >
                Sign In
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <SEO
        title="My Favorite Camps | CampWhiz"
        description="View and manage your favorite summer camps. Access your saved camps and plan your child's summer activities."
        keywords="favorite camps, saved camps, summer camp favorites, camp planning"
        url="/favorites"
      />
      <div className="page-container">
        <div className="page-content">
          <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-semibold text-gray-900 mb-8">My Favorite Camps</h1>

        {isLoading ? (
          <LoadingState message="Loading your favorite camps..." />
        ) : camps.length === 0 ? (
          <div className="text-center py-16 sm:py-20 bg-white rounded-lg shadow">
            {/* Heart icon above content */}
            <div className="flex justify-center mb-6">
              <HeartIcon className="h-24 w-24 sm:h-32 sm:w-32 text-primary/10" />
            </div>
            
            {/* Content */}
            <div>
              <h2 className="text-2xl sm:text-3xl font-semibold text-gray-900 mb-3">
                Start building your camp library
              </h2>
              <p className="text-base sm:text-lg text-gray-600 mb-6 max-w-md mx-auto">
                Save your favorite camps to compare dates, build your calendar, and plan your season. Your favorites are saved forever.
              </p>
              <button
                onClick={() => navigate('/')}
                className="btn btn-primary"
              >
                Browse Camps
              </button>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {camps.map((camp) => (
              <CampCard
                key={camp.id}
                camp={camp}
                onFavoriteChange={refetch}
              />
            ))}
          </div>
        )}
      </div>
    </div>
    </>
  );
}